import { Injectable, OnDestroy } from '@angular/core';
import { Calendar, CalendarType } from '@vendasta/meetings';
import {
  combineLatest,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { GROUP, SERVICE } from '../../constants';
import { CategoryRow, partitionByPinPriority } from './events-group-and-service.service';
import { MeetingListSharedDataService } from '../shared/shared-table-view.service';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { TranslateService } from '@ngx-translate/core';
import { EventTypeSummaryInterface } from '@vendasta/meetings';
import { DurationPipe } from '@galaxy/crm/integrations/meeting-event/src/pipes';

@Injectable()
export class MeetingListLoadDataService implements OnDestroy {
  private unsubscribe$ = new Subject<void>();
  private allEventTypes$: Observable<EventTypeSummaryInterface[]>;
  personalCalendar$: Observable<Calendar>;
  groupCalendars$: Observable<Calendar[]>;
  allGroupCalendarIds$: Observable<string[]>;
  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly sharedDataService: MeetingListSharedDataService,
    private readonly translate: TranslateService,
  ) {}

  getEventTypeData() {
    this.sharedDataService.setPersonalDefaultCalendar(this.personalCalendar$);
    this.sharedDataService.setGroupCalendar(this.groupCalendars$);

    this.allGroupCalendarIds$ = this.groupCalendars$.pipe(
      map((calendars) => calendars?.map((calendar) => calendar.id) || []),
      startWith([]),
    );

    this.allGroupCalendarIds$
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((calendarIds) =>
          calendarIds.length
            ? forkJoin(
                calendarIds.map((calendarId) => {
                  return this.meetingSchedulerStoreService.getGroupCalendarDataFromState(calendarId)
                    ? of(null)
                    : this.meetingSchedulerStoreService.loadCalendar(calendarId);
                }),
              )
            : of([]),
        ),
      )
      .subscribe();

    return this.allEventTypes$.pipe(
      map((eventTypes) =>
        eventTypes.map((et) => ({
          ...et,
          meetingTypeSlug: et.slug,
          duration: DurationPipe.transformDuration(`${et.duration}s`),
          eventType:
            et.calendarType === CalendarType.CALENDAR_TYPE_PERSONAL
              ? this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.PERSONAL_EVENT')
              : et.calendarType === CalendarType.CALENDAR_TYPE_GROUP
                ? this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TEAM_EVENT')
                : this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.SESSION_EVENT'),
        })),
      ),
      shareReplay(1),
    );
  }

  loadGroupOrServiceData(selectedToggle): Observable<any[]> {
    this.personalCalendar$ = this.meetingSchedulerStoreService.loadPersonalCalendar();
    this.groupCalendars$ = this.meetingSchedulerStoreService.loadGroupCalendars();

    let data$: Observable<any[]>;
    switch (selectedToggle) {
      case GROUP:
        this.allEventTypes$ = of([]);
        data$ = this.loadGroupsWithDefaults();
        break;
      case SERVICE:
        this.allEventTypes$ = of([]);
        data$ = this.meetingSchedulerStoreService.loadServices();
        break;
      default:
        this.allEventTypes$ = this.meetingSchedulerStoreService.loadAllEventTypes().pipe(shareReplay(1));
        data$ = this.getEventTypeData();
    }
    return this.sortData(data$);
  }

  private loadGroupsWithDefaults(): Observable<any[]> {
    const groups$ = this.meetingSchedulerStoreService.loadGroups();
    const groupGeneralLinks$ = this.meetingSchedulerStoreService.loadGroupGeneralBookingLinks();

    return combineLatest([groups$, this.personalCalendar$, this.groupCalendars$, groupGeneralLinks$]).pipe(
      map(([groups, personalCalendar, groupCalendars, groupGeneralLinks]) => {
        const defaultGroups: Partial<CategoryRow>[] = [];

        if (personalCalendar?.bookingUrl) {
          defaultGroups.push({
            name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_PERSONAL_GROUP.NAME'),
            description: this.translate.instant(
              'MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_PERSONAL_GROUP.DESCRIPTION',
            ),
            bookingUrl: personalCalendar.bookingUrl,
            isDefaultGroup: true,
            isPinned: true,
          });
        }

        for (const calendar of groupCalendars || []) {
          const bookingUrl = groupGeneralLinks[calendar.id];
          if (bookingUrl) {
            defaultGroups.push({
              name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_TEAM_GROUP.NAME', {
                teamName: calendar.displayName,
              }),
              description: this.translate.instant(
                'MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_TEAM_GROUP.DESCRIPTION',
                { teamName: calendar.displayName },
              ),
              bookingUrl,
              isDefaultGroup: true,
              isPinned: true,
            });
          }
        }

        return [...defaultGroups, ...groups];
      }),
    );
  }

  sortData(data$: Observable<any[]>): Observable<any[]> {
    return data$.pipe(
      map((data) => {
        if (Array.isArray(data)) {
          return this.applySorting(data);
        }
        return [];
      }),
    );
  }

  private applySorting(data: any[]): any[] {
    const nameSortFn = (a: any, b: any) => {
      const nameA = a.name ? a.name.toLowerCase() : '';
      const nameB = b.name ? b.name.toLowerCase() : '';
      return nameA.localeCompare(nameB);
    };
    const [defaultGroups, pinned, unpinned] = partitionByPinPriority(data);
    return [...defaultGroups, ...pinned.sort(nameSortFn), ...unpinned.sort(nameSortFn)];
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
